/**
 * API client.
 *
 * TOKEN HANDLING
 * The access token lives in memory only — never in localStorage. A token in
 * localStorage is readable by any script on the page, which turns any XSS into
 * a durable session theft. Keeping it in a module variable means a successful
 * XSS still cannot persist the token or survive a reload.
 *
 * The refresh token is an httpOnly cookie the browser attaches automatically to
 * the one route that accepts it. JavaScript never sees it.
 *
 * TRANSPARENT REFRESH
 * On a 401 the client refreshes once and replays the original request. Requests
 * that fail during a refresh queue behind it rather than each triggering their
 * own — without that, a dashboard firing six parallel requests would start six
 * refreshes, and refresh-token rotation would treat five of them as token
 * reuse and revoke the session. That failure mode is subtle and total, so the
 * single-flight guard below is load-bearing.
 */

import i18n from '../i18n';

let accessToken: string | null = null;

/** Notified when the session ends, so the app can route back to sign-in. */
type SessionEndedHandler = () => void;
let onSessionEnded: SessionEndedHandler = () => {};

export function setAccessToken(token: string | null) {
  accessToken = token;
}
export function getAccessToken() {
  return accessToken;
}
export function setSessionEndedHandler(handler: SessionEndedHandler) {
  onSessionEnded = handler;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    /** Stable machine code the UI translates, e.g. 'INSUFFICIENT_CREDITS'. */
    readonly code: string,
    readonly details?: Record<string, unknown>,
  ) {
    super(code);
    this.name = 'ApiError';
  }
}

/** The in-flight refresh, if any. All concurrent 401s await this one promise. */
let refreshInFlight: Promise<boolean> | null = null;

async function refreshSession(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    try {
      const response = await fetch('/api/auth/refresh', {
        method: 'POST',
        credentials: 'include', // send the httpOnly refresh cookie
      });
      if (!response.ok) return false;
      const data = (await response.json()) as { accessToken: string };
      accessToken = data.accessToken;
      return true;
    } catch {
      return false;
    } finally {
      // Cleared in a microtask so every awaiting caller sees the same result
      // before the slot is freed for a future refresh.
      queueMicrotask(() => {
        refreshInFlight = null;
      });
    }
  })();

  return refreshInFlight;
}

/**
 * Restore a session from the httpOnly refresh cookie, THROUGH the single-flight
 * guard above.
 *
 * Exported because `AuthProvider` used to call `api.post('/auth/refresh')`
 * directly on mount. That went around the guard entirely — so React StrictMode
 * double-invoking the effect, or two tabs loading together, fired concurrent
 * rotations. The server raised a 500 on the race and, when it did not, read the
 * second request as token reuse and revoked the whole family: people were
 * signed out mid-session for doing nothing wrong.
 *
 * The guard's own comment called this out as the failure mode it existed to
 * prevent. The one caller that bypassed it is the one that hit it.
 */
export async function resumeSession(): Promise<boolean> {
  return refreshSession();
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  /** Internal: prevents an infinite refresh loop. */
  _isRetry?: boolean;
  signal?: AbortSignal;
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, _isRetry = false, signal } = options;

  const headers: Record<string, string> = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  // Tell the API which language to serve CONTENT in (question text, option
  // labels, control titles…). The server overlays translations for this locale
  // and falls back to English; UI strings are still localised client-side.
  // Read lazily from the live i18n instance so a mid-session language switch is
  // reflected on the very next request.
  headers['X-Locale'] = i18n.language || 'en';

  const response = await fetch(path.startsWith('/api') ? path : `/api${path}`, {
    method,
    headers,
    credentials: 'include',
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    ...(signal ? { signal } : {}),
  });

  // 401 → try exactly one refresh, then replay. Auth routes are excluded:
  // a failed sign-in must not trigger a refresh attempt.
  if (response.status === 401 && !_isRetry && !path.includes('/auth/')) {
    const refreshed = await refreshSession();
    if (refreshed) return apiRequest<T>(path, { ...options, _isRetry: true });
    accessToken = null;
    onSessionEnded();
    throw new ApiError(401, 'AUTH_REQUIRED');
  }

  if (response.status === 204) return undefined as T;

  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    const error = (payload as { error?: { code: string; details?: Record<string, unknown> } } | null)
      ?.error;
    throw new ApiError(response.status, error?.code ?? 'UNKNOWN_ERROR', error?.details);
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => apiRequest<T>(path, { method: 'GET', signal }),
  post: <T>(path: string, body?: unknown) => apiRequest<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) => apiRequest<T>(path, { method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown) => apiRequest<T>(path, { method: 'PUT', body }),
  delete: <T>(path: string) => apiRequest<T>(path, { method: 'DELETE' }),
};

/** Build a query string, omitting empty values so filters stay tidy. */
export function qs(params: Record<string, unknown>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const str = search.toString();
  return str ? `?${str}` : '';
}
